/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {Attribute, Component, ContentChild, ContentChildren, Directive, Host, HostBinding, HostListener, Inject, Injectable, Input, NgModule, Optional, Output, Pipe, Self, SkipSelf, ViewChild, ViewChildren, animate, group, keyframes, sequence, state, style, transition, trigger} from '@angular/core';

import {ReflectorReader} from '../private_import_core';

import {StaticSymbol, StaticSymbolCache} from './static_symbol';
import {ResolvedStaticSymbol, StaticSymbolResolver} from './static_symbol_resolver';

const /** @type {?} */ ANGULAR_IMPORT_LOCATIONS = {
  coreDecorators: '@angular/core/src/metadata',
  diDecorators: '@angular/core/src/di/metadata',
  diMetadata: '@angular/core/src/di/metadata',
  diOpaqueToken: '@angular/core/src/di/opaque_token',
  animationMetadata: '@angular/core/src/animation/metadata',
  provider: '@angular/core/src/di/provider'
};

const /** @type {?} */ HIDDEN_KEY = /^\$.*\$$/;
/**
 *  A static reflector implements enough of the Reflector API that is necessary to compile
  * templates statically.
 */
export class StaticReflector implements ReflectorReader {
private annotationCache = new Map<StaticSymbol, any[]>();
private propertyCache = new Map<StaticSymbol, {[key: string]: any[]}>();
private parameterCache = new Map<StaticSymbol, any[]>();
private methodCache = new Map<StaticSymbol, {[key: string]: boolean}>();
private conversionMap = new Map<StaticSymbol, (context: StaticSymbol, args: any[]) => any>();
private opaqueToken: StaticSymbol;
/**
 * @param {?} symbolResolver
 * @param {?=} knownMetadataClasses
 * @param {?=} knownMetadataFunctions
 * @param {?=} errorRecorder
 */
constructor(
private symbolResolver: StaticSymbolResolver,
      knownMetadataClasses: {name: string, filePath: string, ctor: any}[] = [],
      knownMetadataFunctions: {name: string, filePath: string, fn: any}[] = [],
private errorRecorder?: (error: any, fileName: string) => void) {
    this.initializeConversionMap();
    knownMetadataClasses.forEach(
        (kc) => this._registerDecoratorOrConstructor(
            this.getStaticSymbol(kc.filePath, kc.name), kc.ctor));
    knownMetadataFunctions.forEach(
        (kf) => this._registerFunction(this.getStaticSymbol(kf.filePath, kf.name), kf.fn));
  }
/**
 * @param {?} typeOrFunc
 * @return {?}
 */
importUri(typeOrFunc: StaticSymbol): string {
    const /** @type {?} */ staticSymbol = this.findSymbolDeclaration(typeOrFunc);
    return staticSymbol ? staticSymbol.filePath : null;
  }
/**
 * @param {?} name
 * @param {?} moduleUrl
 * @return {?}
 */
resolveIdentifier(name: string, moduleUrl: string): StaticSymbol {
    return this.findDeclaration(moduleUrl, name);
  }
/**
 * @param {?} moduleUrl
 * @param {?} name
 * @param {?=} containingFile
 * @return {?}
 */
findDeclaration(moduleUrl: string, name: string, containingFile?: string): StaticSymbol {
    return this.findSymbolDeclaration(
        this.symbolResolver.getSymbolByModule(moduleUrl, name, containingFile));
  }
/**
 * @param {?} symbol
 * @return {?}
 */
findSymbolDeclaration(symbol: StaticSymbol): StaticSymbol {
    const /** @type {?} */ resolvedSymbol = this.symbolResolver.resolveSymbol(symbol);
    if (resolvedSymbol && resolvedSymbol.metadata instanceof StaticSymbol) {
      return this.findSymbolDeclaration(resolvedSymbol.metadata);
    } else {
      return symbol;
    }
  }
/**
 * @param {?} enumIdentifier
 * @param {?} name
 * @return {?}
 */
resolveEnum(enumIdentifier: any, name: string): any {
    const /** @type {?} */ staticSymbol: StaticSymbol = enumIdentifier;
    return this.getStaticSymbol(staticSymbol.filePath, staticSymbol.name, [name]);
  }
/**
 * @param {?} type
 * @return {?}
 */
public annotations(type: StaticSymbol): any[] {
    let /** @type {?} */ annotations = this.annotationCache.get(type);
    if (!annotations) {
      annotations = [];
      const /** @type {?} */ classMetadata = this.getTypeMetadata(type);
      if (classMetadata['extends']) {
        const /** @type {?} */ parentAnnotations = this.annotations(this.simplify(type, classMetadata['extends']));
        annotations.push(...parentAnnotations);
      }
      if (classMetadata['decorators']) {
        const /** @type {?} */ ownAnnotations: any[] = this.simplify(type, classMetadata['decorators']);
        annotations.push(...ownAnnotations);
      }
      this.annotationCache.set(type, annotations.filter(ann => !!ann));
    }
    return annotations;
  }
/**
 * @param {?} type
 * @return {?}
 */
public propMetadata(type: StaticSymbol): {[key: string]: any[]} {
    let /** @type {?} */ propMetadata = this.propertyCache.get(type);
    if (!propMetadata) {
      const /** @type {?} */ classMetadata = this.getTypeMetadata(type);
      propMetadata = {};
      if (classMetadata['extends']) {
        const /** @type {?} */ parentPropMetadata = this.propMetadata(this.simplify(type, classMetadata['extends']));
        Object.keys(parentPropMetadata).forEach((parentProp) => {
          propMetadata[parentProp] = parentPropMetadata[parentProp];
        });
      }

      const /** @type {?} */ members = classMetadata['members'] || {};
      Object.keys(members).forEach((propName) => {
        const /** @type {?} */ propData = members[propName];
        const /** @type {?} */ prop = ( /** @type {?} */((<any[]>propData)))
                         .find(a => a['__symbolic'] == 'property' || a['__symbolic'] == 'method');
        const /** @type {?} */ decorators: any[] = [];
        if (propMetadata[propName]) {
          decorators.push(...propMetadata[propName]);
        }
        propMetadata[propName] = decorators;
        if (prop && prop['decorators']) {
          decorators.push(...this.simplify(type, prop['decorators']));
        }
      });
      this.propertyCache.set(type, propMetadata);
    }
    return propMetadata;
  }
/**
 * @param {?} type
 * @return {?}
 */
public parameters(type: StaticSymbol): any[] {
    if (!(type instanceof StaticSymbol)) {
      this.reportError(
          new Error(`parameters received ${JSON.stringify(type)} which is not a StaticSymbol`),
          type);
      return [];
    }
    try {
      let /** @type {?} */ parameters = this.parameterCache.get(type);
      if (!parameters) {
        const /** @type {?} */ classMetadata = this.getTypeMetadata(type);
        const /** @type {?} */ members = classMetadata ? classMetadata['members'] : null;
        const /** @type {?} */ ctorData = members ? members['__ctor__'] : null;
        if (ctorData) {
          const /** @type {?} */ ctor = ( /** @type {?} */((<any[]>ctorData))).find(a => a['__symbolic'] == 'constructor');
          const /** @type {?} */ parameterTypes = /** @type {?} */(( <any[]>this.simplify(type, ctor['parameters'] || [])));
          const /** @type {?} */ parameterDecorators = /** @type {?} */(( <any[]>this.simplify(type, ctor['parameterDecorators'] || [])));
          parameters = [];
          parameterTypes.forEach((paramType, index) => {
            const /** @type {?} */ nestedResult: any[] = [];
            if (paramType) {
              nestedResult.push(paramType);
            }
            const /** @type {?} */ decorators = parameterDecorators ? parameterDecorators[index] : null;
            if (decorators) {
              nestedResult.push(...decorators);
            }
            parameters.push(nestedResult);
          });
        } else if (classMetadata['extends']) {
          parameters = this.parameters(this.simplify(type, classMetadata['extends']));
        }
        if (!parameters) {
          parameters = [];
        }
        this.parameterCache.set(type, parameters);
      }
      return parameters;
    } catch ( /** @type {?} */e) {
      console.error(`Failed on type ${JSON.stringify(type)} with error ${e}`);
      throw e;
    }
  }
/**
 * @param {?} type
 * @return {?}
 */
private _methodNames(type: any): {[key: string]: boolean} {
    let /** @type {?} */ methodNames = this.methodCache.get(type);
    if (!methodNames) {
      const /** @type {?} */ classMetadata = this.getTypeMetadata(type);
      methodNames = {};
      if (classMetadata['extends']) {
        const /** @type {?} */ parentMethodNames = this._methodNames(this.simplify(type, classMetadata['extends']));
        Object.keys(parentMethodNames).forEach((parentProp) => {
          methodNames[parentProp] = parentMethodNames[parentProp];
        });
      }

      const /** @type {?} */ members = classMetadata['members'] || {};
      Object.keys(members).forEach((propName) => {
        const /** @type {?} */ propData = members[propName];
        const /** @type {?} */ isMethod = ( /** @type {?} */((<any[]>propData))).some(a => a['__symbolic'] == 'method');
        methodNames[propName] = methodNames[propName] || isMethod;
      });
      this.methodCache.set(type, methodNames);
    }
    return methodNames;
  }
/**
 * @param {?} type
 * @param {?} lcProperty
 * @return {?}
 */
hasLifecycleHook(type: any, lcProperty: string): boolean {
    if (!(type instanceof StaticSymbol)) {
      this.reportError(
          new Error(
              `hasLifecycleHook received ${JSON.stringify(type)} which is not a StaticSymbol`),
          type);
    }
    try {
      return !!this._methodNames(type)[lcProperty];
    } catch ( /** @type {?} */e) {
      console.error(`Failed on type ${JSON.stringify(type)} with error ${e}`);
      throw e;
    }
  }
/**
 * @param {?} type
 * @param {?} ctor
 * @return {?}
 */
private _registerDecoratorOrConstructor(type: StaticSymbol, ctor: any): void {
    this.conversionMap.set(type, (context: StaticSymbol, args: any[]) => new ctor(...args));
  }
/**
 * @param {?} type
 * @param {?} fn
 * @return {?}
 */
private _registerFunction(type: StaticSymbol, fn: any): void {
    this.conversionMap.set(type, (context: StaticSymbol, args: any[]) => fn.apply(undefined, args));
  }
/**
 * @return {?}
 */
private initializeConversionMap(): void {
    const {coreDecorators, diDecorators, diMetadata, diOpaqueToken, animationMetadata, provider} =
        ANGULAR_IMPORT_LOCATIONS;
    this.opaqueToken = this.findDeclaration(diOpaqueToken, 'OpaqueToken');

    this._registerDecoratorOrConstructor(this.findDeclaration(diDecorators, 'Host'), Host);
    this._registerDecoratorOrConstructor(
        this.findDeclaration(diDecorators, 'Injectable'), Injectable);
    this._registerDecoratorOrConstructor(this.findDeclaration(diDecorators, 'Self'), Self);
    this._registerDecoratorOrConstructor(this.findDeclaration(diDecorators, 'SkipSelf'), SkipSelf);
    this._registerDecoratorOrConstructor(this.findDeclaration(diDecorators, 'Inject'), Inject);
    this._registerDecoratorOrConstructor(this.findDeclaration(diDecorators, 'Optional'), Optional);
    this._registerDecoratorOrConstructor(
        this.findDeclaration(coreDecorators, 'Attribute'), Attribute);
    this._registerDecoratorOrConstructor(
        this.findDeclaration(coreDecorators, 'ContentChild'), ContentChild);
    this._registerDecoratorOrConstructor(
        this.findDeclaration(coreDecorators, 'ContentChildren'), ContentChildren);
    this._registerDecoratorOrConstructor(
        this.findDeclaration(coreDecorators, 'ViewChild'), ViewChild);
    this._registerDecoratorOrConstructor(
        this.findDeclaration(coreDecorators, 'ViewChildren'), ViewChildren);
    this._registerDecoratorOrConstructor(this.findDeclaration(coreDecorators, 'Input'), Input);
    this._registerDecoratorOrConstructor(this.findDeclaration(coreDecorators, 'Output'), Output);
    this._registerDecoratorOrConstructor(this.findDeclaration(coreDecorators, 'Pipe'), Pipe);
    this._registerDecoratorOrConstructor(
        this.findDeclaration(coreDecorators, 'HostBinding'), HostBinding);
    this._registerDecoratorOrConstructor(
        this.findDeclaration(coreDecorators, 'HostListener'), HostListener);
    this._registerDecoratorOrConstructor(
        this.findDeclaration(coreDecorators, 'Directive'), Directive);
    this._registerDecoratorOrConstructor(
        this.findDeclaration(coreDecorators, 'Component'), Component);
    this._registerDecoratorOrConstructor(
        this.findDeclaration(coreDecorators, 'NgModule'), NgModule);

    // Note: Some metadata classes can be used directly with Provider.deps.
    this._registerDecoratorOrConstructor(this.findDeclaration(diMetadata, 'Host'), Host);
    this._registerDecoratorOrConstructor(this.findDeclaration(diMetadata, 'Self'), Self);
    this._registerDecoratorOrConstructor(this.findDeclaration(diMetadata, 'SkipSelf'), SkipSelf);
    this._registerDecoratorOrConstructor(this.findDeclaration(diMetadata, 'Optional'), Optional);

    this._registerFunction(this.findDeclaration(animationMetadata, 'trigger'), trigger);
    this._registerFunction(this.findDeclaration(animationMetadata, 'state'), state);
    this._registerFunction(this.findDeclaration(animationMetadata, 'transition'), transition);
    this._registerFunction(this.findDeclaration(animationMetadata, 'style'), style);
    this._registerFunction(this.findDeclaration(animationMetadata, 'animate'), animate);
    this._registerFunction(this.findDeclaration(animationMetadata, 'keyframes'), keyframes);
    this._registerFunction(this.findDeclaration(animationMetadata, 'sequence'), sequence);
    this._registerFunction(this.findDeclaration(animationMetadata, 'group'), group);
  }
/**
 *  getStaticSymbol produces a Type whose metadata is known but whose implementation is not loaded.
  * All types passed to the StaticResolver should be pseudo-types returned by this method.
  * *
 * @param {?} declarationFile the absolute path of the file where the symbol is declared
 * @param {?} name the name of the type.
 * @param {?=} members
 * @return {?}
 */
getStaticSymbol(declarationFile: string, name: string, members?: string[]): StaticSymbol {
    return this.symbolResolver.getStaticSymbol(declarationFile, name, members);
  }
/**
 * @param {?} error
 * @param {?} context
 * @param {?=} path
 * @return {?}
 */
private reportError(error: Error, context: StaticSymbol, path?: string) {
    if (this.errorRecorder) {
      this.errorRecorder(error, (context && context.filePath) || path);
    } else {
      throw error;
    }
  }
/**
 * @param {?} context
 * @param {?} value
 * @return {?}
 */
public simplify(context: StaticSymbol, value: any): any {
    const /** @type {?} */ self = this;
    let /** @type {?} */ scope = BindingScope.empty;
    const /** @type {?} */ calling = new Map<StaticSymbol, boolean>();
/**
 * @param {?} context
 * @param {?} value
 * @param {?} depth
 * @return {?}
 */
function simplifyInContext(context: StaticSymbol, value: any, depth: number): any {
/**
 * @param {?} staticSymbol
 * @return {?}
 */
function resolveReferenceValue(staticSymbol: StaticSymbol): any {
        const /** @type {?} */ resolvedSymbol = self.symbolResolver.resolveSymbol(staticSymbol);
        return resolvedSymbol ? resolvedSymbol.metadata : null;
      }
/**
 * @param {?} functionSymbol
 * @param {?} targetFunction
 * @param {?} args
 * @return {?}
 */
function simplifyCall(functionSymbol: StaticSymbol, targetFunction: any, args: any[]) {
        if (targetFunction && targetFunction['__symbolic'] == 'function') {
          if (calling.get(functionSymbol)) {
            throw new Error('Recursion not supported');
          }
          calling.set(functionSymbol, true);
          try {
            const /** @type {?} */ value = targetFunction['value'];
            if (value && (depth != 0 || value.__symbolic != 'error')) {
              const /** @type {?} */ parameters: string[] = targetFunction['parameters'];
              const /** @type {?} */ defaults: any[] = targetFunction.defaults;
              if (defaults && defaults.length > args.length) {
                args.push(...defaults.slice(args.length).map((value: any) => simplify(value)));
              }
              const /** @type {?} */ functionScope = BindingScope.build();
              for (let /** @type {?} */ i = 0; i < parameters.length; i++) {
                functionScope.define(parameters[i], args[i]);
              }
              const /** @type {?} */ oldScope = scope;
              let /** @type {?} */ result: any;
              try {
                scope = functionScope.done();
                result = simplifyInContext(functionSymbol, value, depth + 1);
              } finally {
                scope = oldScope;
              }
              return result;
            }
          } finally {
            calling.delete(functionSymbol);
          }
        }

        if (depth === 0) {
          // If depth is 0 we are evaluating the top level expression that is describing element
          // decorator. In this case, it is a decorator we don't understand, such as a custom
          // non-angular decorator, and we should just ignore it.
          return {__symbolic: 'ignore'};
        }
        return simplify(
            {__symbolic: 'error', message: 'Function call not supported', context: functionSymbol});
      }
/**
 * @param {?} expression
 * @return {?}
 */
function simplify(expression: any): any {
        if (isPrimitive(expression)) {
          return expression;
        }
        if (expression instanceof Array) {
          const /** @type {?} */ result: any[] = [];
          for (const /** @type {?} */ item of ( /** @type {?} */((<any>expression)))) {
            // Check for a spread expression
            if (item && item.__symbolic === 'spread') {
              const /** @type {?} */ spreadArray = simplify(item.expression);
              if (Array.isArray(spreadArray)) {
                for (const /** @type {?} */ spreadItem of spreadArray) {
                  result.push(spreadItem);
                }
                continue;
              }
            }
            const /** @type {?} */ value = simplify(item);
            if (shouldIgnore(value)) {
              continue;
            }
            result.push(value);
          }
          return result;
        }
        if (expression instanceof StaticSymbol) {
          // Stop simplification at builtin symbols
          if (expression === self.opaqueToken || self.conversionMap.has(expression)) {
            return expression;
          } else {
            const /** @type {?} */ staticSymbol = expression;
            const /** @type {?} */ declarationValue = resolveReferenceValue(staticSymbol);
            if (declarationValue) {
              return simplifyInContext(staticSymbol, declarationValue, depth + 1);
            } else {
              return staticSymbol;
            }
          }
        }
        if (expression) {
          if (expression['__symbolic']) {
            let /** @type {?} */ staticSymbol: StaticSymbol;
            switch (expression['__symbolic']) {
              case 'binop':
                let /** @type {?} */ left = simplify(expression['left']);
                if (shouldIgnore(left)) return left;
                let /** @type {?} */ right = simplify(expression['right']);
                if (shouldIgnore(right)) return right;
                switch (expression['operator']) {
                  case '&&':
                    return left && right;
                  case '||':
                    return left || right;
                  case '|':
                    return left | right;
                  case '^':
                    return left ^ right;
                  case '&':
                    return left & right;
                  case '==':
                    return left == right;
                  case '!=':
                    return left != right;
                  case '===':
                    return left === right;
                  case '!==':
                    return left !== right;
                  case '<':
                    return left < right;
                  case '>':
                    return left > right;
                  case '<=':
                    return left <= right;
                  case '>=':
                    return left >= right;
                  case '<<':
                    return left << right;
                  case '>>':
                    return left >> right;
                  case '+':
                    return left + right;
                  case '-':
                    return left - right;
                  case '*':
                    return left * right;
                  case '/':
                    return left / right;
                  case '%':
                    return left % right;
                }
                return null;
              case 'if':
                let /** @type {?} */ condition = simplify(expression['condition']);
                return condition ? simplify(expression['thenExpression']) :
                                   simplify(expression['elseExpression']);
              case 'pre':
                let /** @type {?} */ operand = simplify(expression['operand']);
                if (shouldIgnore(operand)) return operand;
                switch (expression['operator']) {
                  case '+':
                    return operand;
                  case '-':
                    return -operand;
                  case '!':
                    return !operand;
                  case '~':
                    return ~operand;
                }
                return null;
              case 'index':
                let /** @type {?} */ indexTarget = simplify(expression['expression']);
                let /** @type {?} */ index = simplify(expression['index']);
                if (indexTarget && isPrimitive(index)) return indexTarget[index];
                return null;
              case 'select':
                const /** @type {?} */ member = expression['member'];
                let /** @type {?} */ selectContext = context;
                let /** @type {?} */ selectTarget = simplify(expression['expression']);
                if (selectTarget instanceof StaticSymbol) {
                  const /** @type {?} */ members = selectTarget.members.concat(member);
                  selectContext =
                      self.getStaticSymbol(selectTarget.filePath, selectTarget.name, members);
                  const /** @type {?} */ declarationValue = resolveReferenceValue(selectContext);
                  if (declarationValue) {
                    return simplifyInContext(selectContext, declarationValue, depth + 1);
                  } else {
                    return selectContext;
                  }
                }
                if (selectTarget && isPrimitive(member))
                  return simplifyInContext(selectContext, selectTarget[member], depth + 1);
                return null;
              case 'reference':
                // Note: This only has to deal with variable references,
                // as symbol references have been converted into StaticSymbols already
                // in the StaticSymbolResolver!
                const /** @type {?} */ name: string = expression['name'];
                const /** @type {?} */ localValue = scope.resolve(name);
                if (localValue != BindingScope.missing) {
                  return localValue;
                }
                break;
              case 'class':
                return context;
              case 'function':
                return context;
              case 'new':
              case 'call':
                // Determine if the function is a built-in conversion
                staticSymbol = simplifyInContext(context, expression['expression'], depth + 1);
                if (staticSymbol instanceof StaticSymbol) {
                  if (staticSymbol === self.opaqueToken) {
                    // if somebody calls new OpaqueToken, don't create an OpaqueToken,
                    // but rather return the symbol to which the OpaqueToken is assigned to.
                    return context;
                  }
                  const /** @type {?} */ argExpressions: any[] = expression['arguments'] || [];
                  const /** @type {?} */ args =
                      argExpressions.map(arg => simplifyInContext(context, arg, depth + 1));
                  let /** @type {?} */ converter = self.conversionMap.get(staticSymbol);
                  if (converter) {
                    return converter(context, args);
                  } else {
                    // Determine if the function is one we can simplify.
                    const /** @type {?} */ targetFunction = resolveReferenceValue(staticSymbol);
                    return simplifyCall(staticSymbol, targetFunction, args);
                  }
                }
                break;
              case 'error':
                let /** @type {?} */ message = produceErrorMessage(expression);
                if (expression['line']) {
                  message =
                      `${message} (position ${expression['line']+1}:${expression['character']+1} in the original .ts file)`;
                  throw positionalError(
                      message, context.filePath, expression['line'], expression['character']);
                }
                throw new Error(message);
            }
            return null;
          }
          return mapStringMap(expression, (value, name) => simplify(value));
        }
        return null;
      }

      try {
        return simplify(value);
      } catch ( /** @type {?} */e) {
        const /** @type {?} */ members = context.members.length ? `.${context.members.join('.')}` : '';
        const /** @type {?} */ message =
            `${e.message}, resolving symbol ${context.name}${members} in ${context.filePath}`;
        if (e.fileName) {
          throw positionalError(message, e.fileName, e.line, e.column);
        }
        throw new Error(message);
      }
    }

    const /** @type {?} */ recordedSimplifyInContext = (context: StaticSymbol, value: any, depth: number) => {
      try {
        return simplifyInContext(context, value, depth);
      } catch ( /** @type {?} */e) {
        this.reportError(e, context);
      }
    };

    const /** @type {?} */ result = this.errorRecorder ? recordedSimplifyInContext(context, value, 0) :
                                        simplifyInContext(context, value, 0);
    if (shouldIgnore(result)) {
      return undefined;
    }
    return result;
  }
/**
 * @param {?} type
 * @return {?}
 */
private getTypeMetadata(type: StaticSymbol): {[key: string]: any} {
    const /** @type {?} */ resolvedSymbol = this.symbolResolver.resolveSymbol(type);
    return resolvedSymbol && resolvedSymbol.metadata ? resolvedSymbol.metadata :
                                                       {__symbolic: 'class'};
  }
}

function StaticReflector_tsickle_Closure_declarations() {
 /** @type {?} */
StaticReflector.prototype.annotationCache;
 /** @type {?} */
StaticReflector.prototype.propertyCache;
 /** @type {?} */
StaticReflector.prototype.parameterCache;
 /** @type {?} */
StaticReflector.prototype.methodCache;
 /** @type {?} */
StaticReflector.prototype.conversionMap;
 /** @type {?} */
StaticReflector.prototype.opaqueToken;
 /** @type {?} */
StaticReflector.prototype.symbolResolver;
 /** @type {?} */
StaticReflector.prototype.errorRecorder;
}

/**
 * @param {?} error
 * @return {?}
 */
function expandedMessage(error: any): string {
  switch (error.message) {
    case 'Reference to non-exported class':
      if (error.context && error.context.className) {
        return `Reference to a non-exported class ${error.context.className}. Consider exporting the class`;
      }
      break;
    case 'Variable not initialized':
      return 'Only initialized variables and constants can be referenced because the value of this variable is needed by the template compiler';
    case 'Destructuring not supported':
      return 'Referencing an exported destructured variable or constant is not supported by the template compiler. Consider simplifying this to avoid destructuring';
    case 'Could not resolve type':
      if (error.context && error.context.typeName) {
        return `Could not resolve type ${error.context.typeName}`;
      }
      break;
    case 'Function call not supported':
      let /** @type {?} */ prefix =
          error.context && error.context.name ? `Calling function '${error.context.name}', f` : 'F';
      return prefix +
          'unction calls are not supported. Consider replacing the function or lambda with a reference to an exported function';
    case 'Reference to a local symbol':
      if (error.context && error.context.name) {
        return `Reference to a local (non-exported) symbol '${error.context.name}'. Consider exporting the symbol`;
      }
      break;
  }
  return error.message;
}
/**
 * @param {?} error
 * @return {?}
 */
function produceErrorMessage(error: any): string {
  return `Error encountered resolving symbol values statically. ${expandedMessage(error)}`;
}
/**
 * @param {?} input
 * @param {?} transform
 * @return {?}
 */
function mapStringMap(input: {[key: string]: any}, transform: (value: any, key: string) => any):
    {[key: string]: any} {
  if (!input) return {};
  const /** @type {?} */ result: {[key: string]: any} = {};
  Object.keys(input).forEach((key) => {
    const /** @type {?} */ value = transform(input[key], key);
    if (!shouldIgnore(value)) {
      if (HIDDEN_KEY.test(key)) {
        Object.defineProperty(result, key, {enumerable: false, configurable: true, value: value});
      } else {
        result[key] = value;
      }
    }
  });
  return result;
}
/**
 * @param {?} o
 * @return {?}
 */
function isPrimitive(o: any): boolean {
  return o === null || (typeof o !== 'function' && typeof o !== 'object');
}

interface BindingScopeBuilder {
  define(name: string, value: any): BindingScopeBuilder;
  done(): BindingScope;
}
/**
 * @abstract
 */
abstract class BindingScope {
/**
 * @abstract
 * @param {?} name
 * @return {?}
 */
resolve(name: string) {}
public static missing = {};
public static empty: BindingScope = {resolve: name => BindingScope.missing};
/**
 * @return {?}
 */
public static build(): BindingScopeBuilder {
    const /** @type {?} */ current = new Map<string, any>();
    return {
      define: function(name, value) {
        current.set(name, value);
        return this;
      },
      done: function() {
        return current.size > 0 ? new PopulatedScope(current) : BindingScope.empty;
      }
    };
  }
}

function BindingScope_tsickle_Closure_declarations() {
 /** @type {?} */
BindingScope.missing;
 /** @type {?} */
BindingScope.empty;
}


class PopulatedScope extends BindingScope {
/**
 * @param {?} bindings
 */
constructor(private bindings: Map<string, any>) { super(); }
/**
 * @param {?} name
 * @return {?}
 */
resolve(name: string): any {
    return this.bindings.has(name) ? this.bindings.get(name) : BindingScope.missing;
  }
}

function PopulatedScope_tsickle_Closure_declarations() {
 /** @type {?} */
PopulatedScope.prototype.bindings;
}

/**
 * @param {?} a
 * @param {?} b
 * @return {?}
 */
function sameSymbol(a: StaticSymbol, b: StaticSymbol): boolean {
  return a === b;
}
/**
 * @param {?} value
 * @return {?}
 */
function shouldIgnore(value: any): boolean {
  return value && value.__symbolic == 'ignore';
}
/**
 * @param {?} message
 * @param {?} fileName
 * @param {?} line
 * @param {?} column
 * @return {?}
 */
function positionalError(message: string, fileName: string, line: number, column: number): Error {
  const /** @type {?} */ result = new Error(message);
  ( /** @type {?} */((result as any))).fileName = fileName;
  ( /** @type {?} */((result as any))).line = line;
  ( /** @type {?} */((result as any))).column = column;
  return result;
}
interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
